import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FileText, Loader2, MapPin, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg } from "../../lib/api";

const STATUSES = ["new", "contacted", "confirmed", "closed"];
const digits = (p = "") => p.replace(/\D/g, "");

export const EnquiryList = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["enquiries"], queryFn: () => api.get("/admin/enquiries").then((r) => r.data) });
  const rows = Array.isArray(data) ? data : [];

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (isLoading) return <Loader2 className="animate-spin text-[#F3E5AB]" />;
  if (!rows.length) return <p className="text-[#E8D8C8]" data-testid="enquiries-empty">No enquiries yet.</p>;

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="enquiries-list">
      {rows.map((r, i) => (
        <article key={r.id} className="rounded-[20px] border border-[#D4AF37]/20 bg-[#330B2E]/70 p-5" data-testid={`enquiry-row-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-2xl">{r.name}</h3>
              <p className="text-sm text-[#F3E5AB]">{r.category_name} · {r.event_date}</p>
            </div>
            <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
              <SelectTrigger className="h-9 w-32 rounded-full border-[#D4AF37]/30 bg-[#190317]/60 text-xs capitalize" data-testid={`enquiry-status-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#D4AF37]/30 bg-[#1d051a] text-[#FFF8EF]">
                {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {r.location && <p className="mt-3 flex items-center gap-1.5 text-sm text-[#E8D8C8]"><MapPin size={14} className="text-[#D4AF37]" /> {r.location}</p>}
          {r.message && <p className="mt-2 text-sm text-[#E8D8C8]">{r.message}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 text-[#F3E5AB]"><Phone size={14} /> {r.phone}</a>
            <a href={`https://wa.me/${digits(r.phone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#25D366]"><MessageCircle size={14} /> WhatsApp</a>
            <Link to={r.order_id ? `/admin/orders/${r.order_id}` : `/admin/orders/new?enquiry=${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-3 py-1.5 text-xs font-semibold text-[#240620]" data-testid={`enquiry-order-note-${i}`}>
              <FileText size={13} /> {r.order_id ? "View order note" : "Create order note"}
            </Link>
            <span className="ml-auto text-xs text-[#E8D8C8]">{new Date(r.created_at).toLocaleString()}</span>
          </div>
        </article>
      ))}
    </div>
  );
};
